import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import EntityForm from "@/components/EntityForm";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, BookText, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPES = [
  { value: "ventes", label: "Ventes" }, { value: "achats", label: "Achats" },
  { value: "banque", label: "Banque" }, { value: "caisse", label: "Caisse" },
  { value: "paie", label: "Paie" }, { value: "immobilisations", label: "Immobilisations" },
  { value: "od", label: "Opérations diverses" },
];

export default function Journals() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/accounting/journals").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const schema = [
    { name: "code", label: "Code journal (ex : VE)", required: true },
    { name: "type", label: "Type", type: "select", options: TYPES },
    { name: "libelle", label: "Libellé", required: true, col: 2 },
  ];

  const save = async (form) => {
    try {
      if (editing) await api.put(`/accounting/journals/${editing.id}`, form);
      else await api.post("/accounting/journals", form);
      toast.success(editing ? "Journal mis à jour" : "Journal créé"); load();
    } catch (e) { toast.error(apiErr(e)); throw e; }
  };
  const remove = async (id) => { if (!window.confirm("Supprimer ce journal ?")) return; await api.delete(`/accounting/journals/${id}`); toast.success("Supprimé"); load(); };

  return (
    <div>
      <PageHeader title="Journaux comptables" subtitle="Ventes, achats, banque, caisse, paie, OD…">
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-journal" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Nouveau journal</Button>
      </PageHeader>
      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={BookText} title="Aucun journal" subtitle="Créez un journal comptable." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>Code</TableHead><TableHead>Libellé</TableHead><TableHead>Type</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((j) => (
                <TableRow key={j.id} data-testid={`journal-row-${j.code}`} className="hover:bg-slate-50">
                  <TableCell className="font-mono text-sm font-semibold text-brand-700">{j.code}</TableCell>
                  <TableCell>{j.libelle}</TableCell>
                  <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-xs">{j.type}</span></TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`journal-menu-${j.code}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setEditing(j); setOpen(true); }}><Pencil className="h-4 w-4 mr-2" />Modifier</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => remove(j.id)} className="text-rose-600"><Trash2 className="h-4 w-4 mr-2" />Supprimer</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}
      <EntityForm open={open} onOpenChange={setOpen} title={editing ? "Modifier le journal" : "Nouveau journal"} schema={schema} initial={editing || { type: "od" }} onSubmit={save} />
    </div>
  );
}
